/**
 * Огледалото FE → каса (§4 на договора). Докато има интернет, всеки успешен
 * GET към облака по пътищата, които касата ще трябва да сервира без интернет
 * (менюто, методите на плащане, настройките, потребителите, мониторите,
 * списъкът с маси), се препраща на касата с `PUT {edge}/mirror` и тя го
 * пази като последен известен отговор. Извиква се от response интерсептора
 * в `api/client.ts`.
 *
 * Правила: fire-and-forget, timeout 2 s, най-много една чакаща заявка на
 * път (последната печели — предишната се отменя), грешките се гълтат
 * (лог само в dev). Нищо тук не бива да забави или да провали заявката,
 * чийто отговор се огледва.
 */
import axios from "axios";
import { getEdgeBaseUrl, isEdgeLocalMode } from "./edgeState";

const MIRROR_TIMEOUT_MS = 2_000;

/** Пътищата (префикси), които касата сервира без интернет — §4 и §13.3
 * (`/discounts/clients` — клиентите с отстъпка за „Клиенти"; касата връща
 * последния запис за пътя без значение от `restaurant_name` в query-то). */
const MIRRORED_PREFIXES = [
  "/menu/",
  "/menu/stations",
  "/menu/languages",
  "/payment-methods",
  "/configuration/",
  "/user/info",
  "/user/users",
  "/user/organization",
  "/monitors/",
  "/discounts/clients",
];

/** Пътищата с ТОЧНО съвпадение:
 *  • `/bills` обновява cloud view-то на bill store-а, а не таблицата с
 *    огледала; `/bills/...` не се огледва;
 *  • `/reservations/edge-view` — изгледът, от който касата сервира
 *    „Резервации" и „Настани" без интернет (`edgeReservationsFeed.ts`);
 *    `/reservations?date=` НЕ се огледва — касата смята деня сама;
 *  • `/restaurant-schema` — схемата, само за рисуването на платното;
 *    записите под `/restaurant-schema/…` не се огледват. */
const EXACT_MIRRORED_PATHS = new Set([
  "/bills",
  "/reservations/edge-view",
  "/restaurant-schema",
]);

export function shouldMirrorPath(path: string): boolean {
  if (EXACT_MIRRORED_PATHS.has(path)) return true;
  return MIRRORED_PREFIXES.some((prefix) => path.startsWith(prefix));
}

/**
 * Разделя относителния адрес на заявката на път и querystring, като слива
 * `?…` от самия URL с `params` на axios (скаларни стойности; масив →
 * повторен ключ със `[]`, както axios-ият сериализатор).
 */
export function splitRequestUrl(
  url: string | undefined,
  params: unknown,
): { path: string; query: string } {
  const raw = url ?? "";
  const questionMark = raw.indexOf("?");
  const path = questionMark === -1 ? raw : raw.slice(0, questionMark);
  const search = new URLSearchParams(
    questionMark === -1 ? "" : raw.slice(questionMark + 1),
  );
  if (params && typeof params === "object") {
    for (const [key, value] of Object.entries(
      params as Record<string, unknown>,
    )) {
      if (value === undefined || value === null) continue;
      if (Array.isArray(value)) {
        for (const item of value) search.append(`${key}[]`, String(item));
      } else {
        search.set(key, String(value));
      }
    }
  }
  return { path, query: search.toString() };
}

const pendingByPath = new Map<string, AbortController>();

export function mirrorCloudResponse(entry: {
  path: string;
  query: string;
  body: unknown;
  ticket: string | null;
  /** `X-Edge-View-At` на облачния отговор (F4) — по-старо BE не го праща. */
  viewAt?: string | null;
}) {
  const base = getEdgeBaseUrl();
  // Без адрес (резервът е изключен / касата не е обявена), без билет или
  // в локален режим (там четенето и без това идва от касата) няма огледало.
  if (!base || !entry.ticket || isEdgeLocalMode()) return;

  pendingByPath.get(entry.path)?.abort();
  const controller = new AbortController();
  pendingByPath.set(entry.path, controller);

  axios
    .put(
      `${base}/mirror`,
      {
        path: entry.path,
        query: entry.query,
        body: entry.body,
        ...(entry.viewAt ? { view_at: entry.viewAt } : {}),
      },
      {
        headers: { "X-Edge-Ticket": entry.ticket },
        timeout: MIRROR_TIMEOUT_MS,
        signal: controller.signal,
      },
    )
    .catch((error: unknown) => {
      if (import.meta.env.DEV && !axios.isCancel(error)) {
        console.debug("[edge] mirror failed", entry.path, error);
      }
    })
    .finally(() => {
      if (pendingByPath.get(entry.path) === controller) {
        pendingByPath.delete(entry.path);
      }
    });
}

// ─── Кухненският екран към касата (§4.1) ─────────────────────────────────
//
// ЗАЩО е отделно от горното: всичко над този ред е огледало на облачен GET.
// Станционният обхват („отворените поръчки + затворените до 2 часа назад,
// сторнираните редове зачеркнати") НЯМА облачен GET — той идва само по
// WebSocket. А касата не може да го възпроизведе сама: планът, от който се
// замразява, е отговорът на `GET /bills`, а той не носи нито затворените
// сметки, нито сторнираните редове. Затова мониторът огледалва СВОЯ ЕКРАН:
// каквото е показвал последно с интернет, касата го сервира, докато интернет
// няма. Така по конструкция нищо не изчезва от дъската при замразяването.
//
// Пътят е `/bills/ws/station`, станцията е в query-то (`station_id=3`), защото
// касата разпознава handler-ите по ТОЧЕН път (`mirror._path_handlers`).
const STATION_MIRROR_PATH = "/bills/ws/station";

// Кадърът се праща със забавяне: при пълна кухня статусите се сменят по
// няколко пъти в секунда, а касата е на същия LAN и няма нужда да чува всяка
// от тях — интересува я само КАКВО е стояло на екрана последно. Пращаме и
// само при РАЗЛИКА (`lastSentByStation`), за да не тече поток от еднакви тела.
const STATION_MIRROR_DEBOUNCE_MS = 2_000;

const stationTimers = new Map<number, ReturnType<typeof setTimeout>>();
const stationQueued = new Map<number, { body: string; ticket: string }>();
const lastSentByStation = new Map<number, string>();
const stationInFlight = new Map<number, AbortController>();

function flushStationMirror(stationId: number) {
  stationTimers.delete(stationId);
  const queued = stationQueued.get(stationId);
  stationQueued.delete(stationId);
  if (!queued) return;
  const base = getEdgeBaseUrl();
  // Проверката е и тук, не само при заявката: между насрочването и
  // изпращането режимът може да е станал „локален" и тогава кадърът, който
  // щяхме да пратим, вече не е облачната истина, а нашето собствено ехо.
  if (!base || isEdgeLocalMode()) return;

  stationInFlight.get(stationId)?.abort();
  const controller = new AbortController();
  stationInFlight.set(stationId, controller);
  lastSentByStation.set(stationId, queued.body);

  axios
    .put(
      `${base}/mirror`,
      {
        path: STATION_MIRROR_PATH,
        query: `station_id=${stationId}`,
        body: JSON.parse(queued.body),
      },
      {
        headers: { "X-Edge-Ticket": queued.ticket },
        timeout: MIRROR_TIMEOUT_MS,
        signal: controller.signal,
      },
    )
    .catch((error: unknown) => {
      // Неуспехът трие спомена: иначе следващият идентичен кадър би бил
      // пропуснат като „вече изпратен" и касата щеше да остане без него.
      if (!axios.isCancel(error)) lastSentByStation.delete(stationId);
      if (import.meta.env.DEV && !axios.isCancel(error)) {
        console.debug("[edge] station mirror failed", stationId, error);
      }
    })
    .finally(() => {
      if (stationInFlight.get(stationId) === controller) {
        stationInFlight.delete(stationId);
      }
    });
}

/**
 * Огледалва текущия списък на един кухненски монитор към касата. Викa се на
 * ВСЯКА промяна на списъка (initial_data, refresh_data, new_order,
 * item_cancelled, status_update) — без интернет или без билет не прави нищо.
 */
export function mirrorStationOrders(entry: {
  stationId: number;
  orders: unknown[];
  ticket: string | null;
}) {
  const base = getEdgeBaseUrl();
  if (!base || !entry.ticket || isEdgeLocalMode()) return;
  if (!Number.isFinite(entry.stationId)) return;

  const body = JSON.stringify(entry.orders ?? []);
  if (lastSentByStation.get(entry.stationId) === body) return;

  stationQueued.set(entry.stationId, { body, ticket: entry.ticket });
  if (stationTimers.has(entry.stationId)) return;
  stationTimers.set(
    entry.stationId,
    setTimeout(
      () => flushStationMirror(entry.stationId),
      STATION_MIRROR_DEBOUNCE_MS,
    ),
  );
}

/** Само за тестове: забравя таймерите и изпратеното. */
export function resetStationMirrorForTests() {
  for (const timer of stationTimers.values()) clearTimeout(timer);
  stationTimers.clear();
  stationQueued.clear();
  lastSentByStation.clear();
  for (const controller of stationInFlight.values()) controller.abort();
  stationInFlight.clear();
}
